import { Link } from '@mui/material';

import { FormProvider, useForm } from 'react-hook-form';

import {
  Link as RouterLink,
  useNavigate,
  useSearchParams,
} from 'react-router';

import { useEffect } from 'react';

import { ClassValidatorResolver } from '@app-velocity/validators';
import { useI18n } from '@app-velocity/i18n';
import { Form, TextField, ErrorMessage, Button } from '@app-velocity/ui';

import {
  AuthRoutesPaths,
  useUserAuthentication,
} from '@app-velocity/libs/auth';

import { UserRegisterDto } from '../../dto/UserRegisterDto';

import { useRegisterMutation } from '../../queries/useRegisterMutation';
import { Agreement } from './components/Agreement';
import { AuthOptions } from '../../widgets/LoginForm/components/AuthOptions';
import { useAuthProviderContext } from '../../context/useAuthProviderContext';

import { EmailOptIn } from '../../components/EmailOptIn';

export const REGISTRATION_PAGE_TEST_IDS = {
  EMAIL: 'email-registration-test-id',
  PASSWORD: 'password-registration-test-id',
  EULA_PRIVACY_AGREEMENT: 'eula-privacy-agreement-test-id',
  MARKETING_AGREEMENT: 'marketing-agreement-test-id',
  SUBMIT: 'submit-registration-test-id',
};

export const RegisterForm = () => {
  const methods = useForm<UserRegisterDto>({
    mode: 'onSubmit',
    resolver: ClassValidatorResolver(UserRegisterDto),
    defaultValues: {
      marketingAccepted: true,
    },
  });

  const { type: authType, setFormType } = useAuthProviderContext();

  const [params] = useSearchParams();

  const invitation = params.get('invitation');

  const {
    control,
    handleSubmit,
    getValues,
    formState: { errors: formErrors },
  } = methods;

  const navigate = useNavigate();
  const { t } = useI18n();

  const {
    isSuccess,
    mutate: registerUser,
    error: mutationError,
    isPending,
  } = useRegisterMutation();

  const { mutate: authenticateUser, isSuccess: isLoginSuccess } =
    useUserAuthentication();

  useEffect(() => {
    if (isSuccess) {
      authenticateUser({
        email: getValues('email'),
        password: getValues('password'),
      });
    }
  }, [isSuccess]);

  useEffect(() => {
    if (authType === 'detached') return;

    if (isLoginSuccess) navigate('/');
  }, [isLoginSuccess]);

  const onSubmit = (data: UserRegisterDto) => {
    registerUser({
      invitationToken: invitation || undefined,
      email: data.email,
      password: data.password,
      acceptedAgreements: data.acceptedAgreements,
      marketingAccepted: data.marketingAccepted,
    });
  };

  const switchToLogin = () => {
    setFormType('login');
  };

  return (
    <FormProvider {...methods}>
      <Form onSubmit={handleSubmit(onSubmit)} data-testid="form-registration">
        <Form.FieldContainer>
          <Form.Label>{t('users:email')}</Form.Label>
          <TextField
            name="email"
            control={control}
            placeholder={t('users:auth.email_placeholder')}
            fullWidth
            helperText={formErrors.email?.message}
            error={!!formErrors.email}
            inputProps={{
              autoComplete: 'email',
              'data-testid': REGISTRATION_PAGE_TEST_IDS.EMAIL,
            }}
          />
        </Form.FieldContainer>

        <Form.FieldContainer>
          <Form.Label>{t('users:password')}</Form.Label>
          <TextField
            name="password"
            control={control}
            placeholder={t('users:auth.password_placeholder')}
            fullWidth
            type="password"
            helperText={formErrors.password?.message}
            error={!!formErrors.password}
            inputProps={{
              'data-testid': REGISTRATION_PAGE_TEST_IDS.PASSWORD,
            }}
          />
        </Form.FieldContainer>

        <EmailOptIn />
        <Agreement />

        <ErrorMessage error={mutationError} />

        <Form.ButtonsContainer>
          <Button
            fullWidth
            disabled={isPending}
            type="submit"
            variant="contained"
            size="large"
            data-testid={REGISTRATION_PAGE_TEST_IDS.SUBMIT}
          >
            {t('users:auth.sign_up')}
          </Button>
          <AuthOptions />
          <Form.SecondaryText>
            {t('users:auth.already_have_an_account')}{' '}
            {authType === 'detached' ? (
              <Button variant="link" onClick={switchToLogin}>
                {t('users:auth.sign_in')}
              </Button>
            ) : (
              <Link component={RouterLink} to={AuthRoutesPaths.Login}>
                {t('users:auth.sign_in')}
              </Link>
            )}
          </Form.SecondaryText>
        </Form.ButtonsContainer>
      </Form>
    </FormProvider>
  );
};
